import { motion } from "framer-motion";
import { ArrowDownRight, CalendarHeart, Sparkles, Timer } from "lucide-react";

const steps = [
  { icon: Sparkles, t: "Answer 4 fun questions", s: "About your business & competition" },
  { icon: Timer, t: "Wait about a minute", s: "Our AI strategist cooks your plan" },
  { icon: CalendarHeart, t: "Get a 30-day calendar", s: "One easy idea for every single day" },
];

export const Hero = ({ onStart }) => (
  <section className="relative px-6 md:px-12 pt-8 pb-16 grid lg:grid-cols-12 gap-10 items-center">
    <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: .6 }}
      className="lg:col-span-7">
      <p className="text-xs uppercase tracking-[0.3em] text-[var(--muted)] mb-6 flex items-center gap-2">
        <span className="h-2 w-2 rounded-full bg-[var(--gold)]" /> A free DIY game for business owners
      </p>
      <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.02]">
        Play the game.<br />
        <em className="font-serif-cc font-normal text-[1.15em]">Win a month</em> of content<span className="text-[var(--gold)]">.</span>
      </h1>
      <p className="mt-6 text-base md:text-lg text-[var(--muted)] max-w-xl">
        Tell us about your business and your competitor. We'll hand you a cute, easy-to-follow
        30-day social media calendar — no marketing degree needed.
      </p>
      <div className="mt-10 flex flex-wrap items-center gap-5">
        <button onClick={onStart} data-testid="hero-start-game-btn"
          className="group rounded-full bg-[var(--green)] text-[var(--cream)] pl-8 pr-3 py-3 text-lg font-semibold flex items-center gap-4 hover:scale-[1.03] transition-transform shadow-[0_10px_0_-2px_var(--gold)]">
          Start the game
          <span className="h-11 w-11 rounded-full bg-[var(--gold)] text-[var(--green)] grid place-items-center group-hover:rotate-45 transition-transform">
            <ArrowDownRight />
          </span>
        </button>
        <span className="font-hand text-2xl text-[var(--green-2)] -rotate-3">it's free, takes 2 mins!</span>
      </div>
    </motion.div>
    <HeroCard />
    <div className="lg:col-span-12 grid md:grid-cols-3 gap-4 mt-6">
      {steps.map(({ icon: Icon, t, s }, i) => (
        <motion.div key={t} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: .3 + i * .12 }}
          className="flex items-start gap-4 border border-[var(--line)] rounded-2xl p-5 bg-white/60">
          <span className="font-serif-cc text-4xl text-[var(--gold)] leading-none">0{i + 1}</span>
          <div>
            <p className="font-semibold flex items-center gap-2"><Icon size={16} /> {t}</p>
            <p className="text-sm text-[var(--muted)]">{s}</p>
          </div>
        </motion.div>
      ))}
    </div>
  </section>
);

const preview = [["Reel", "#ffd84f"], ["Story", "#b9e3d0"], ["Carousel", "#f6a5c0"], ["Post", "#c7d2fe"]];

const HeroCard = () => (
  <motion.div initial={{ opacity: 0, rotate: 4, y: 30 }} animate={{ opacity: 1, rotate: -3, y: 0 }} transition={{ delay: .2, duration: .7 }}
    className="lg:col-span-5 relative">
    <div className="relative grain overflow-hidden bg-[var(--green)] rounded-[28px] p-6 text-[var(--cream)] shadow-2xl">
      <div className="flex justify-between items-center mb-4">
        <p className="font-serif-cc text-3xl italic">Your Month</p>
        <span className="text-xs uppercase tracking-widest text-[var(--gold)]">30 ideas</span>
      </div>
      <div className="grid grid-cols-7 gap-1.5">
        {Array.from({ length: 28 }).map((_, i) => {
          const [, c] = preview[i % 4];
          return <div key={i} className="aspect-square rounded-lg p-1 text-[10px] font-bold text-[var(--green)]" style={{ background: c, opacity: .35 + ((i * 37) % 65) / 100 }}>{i + 1}</div>;
        })}
      </div>
      <div className="flex gap-2 mt-4 flex-wrap">
        {preview.map(([n, c]) => <span key={n} className="text-xs px-2.5 py-1 rounded-full text-[var(--green)] font-semibold" style={{ background: c }}>{n}</span>)}
      </div>
    </div>
    <div className="floaty absolute -top-6 -right-2 bg-[var(--gold)] text-[var(--green)] rounded-full px-5 py-3 font-hand text-2xl shadow-lg">
      made for you!
    </div>
  </motion.div>
);
